import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTour } from "@reactour/tour";
import {
  onLoadQuiz,
  onFailedQuiz,
  onChecking,
  onLoadRisk,
  onNotChecking,
  onErrorMessage,
  onSuccesMessage,
  onLoadSchedule,
  onCleanSchedule,
  onUpdateState,
  onAddScheduledEventCount,
  onRemoveScheduledEventCount,
  onViewVideoProcedure,
  onViewPdfProcedure,
  openTutorial,
  closeTutorial,
  onNotViewMedia,
  addProcedureEvent,
  removeProcedureEvent,
  addValorationEvent,
  removeValorationEvent,
  removeScheduledEvent,
  loadingDoctorsSelect,
  loadedDoctorsSelect,
  failedDoctorsSelect,
  resetProcedureScheduled,
} from "../store/patient/patientSlice";
import {
  onLoadPatientSurgery,
  onFailedPatientSurgery,
  setQuizAnswered,
} from "../store/patient/patientSurgerySlice";
import { setScheduleEvents } from "../store/patient/scheduleEventSlice";
import { translations, sleep } from "../helpers";
import { gosafeApi } from "../api";

export const usePatientStore = () => {
  const dispatch = useDispatch();
  const {
    isLoadingQuiz,
    isQuiz,
    message,
    id,
    patient,
    procedure,
    state,
    track,
    dasiScore,
    mets,
    cRisk,
    asa,
    summary,
    scheduledEventCount,
    scheduledEventFinishedCount,
    checking,
    errorMessage,
    surgeryRooms,
    schedule,
    description,
    numberEvents,
    viewVideoProcedure,
    viewPdfProcedure,
    isTutorialOpen,
    finishedProcedureEventsCount,
    assessmentEvents,
    procedureEvents,
    finishedProcedure,
    doctorsSelect,
    isProcedureScheduled,
  } = useSelector((state) => state.patient.patient);

  const { patientSurgery } = useSelector(
    (state) => state.patient.patientSurgery
  );

  const { setIsOpen, setSteps, setCurrentStep } = useTour();

  const startDecryptKey = useCallback(
    async (key) => {
      try {
        const { data } = await gosafeApi.get(`patient/quiz?key=${key}`);

        dispatch(onLoadQuiz(data));
        dispatch(setScheduleEvents(data));
        dispatch(onLoadPatientSurgery(data));
        return;
      } catch (error) {
        dispatch(
          onFailedPatientSurgery(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        dispatch(
          onFailedQuiz(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch]
  );

  const startSendQuiz = useCallback(
    async (anwers) => {
      try {
        dispatch(onChecking());
        const body = {
          patientSurgeryId: id,
          responses: anwers,
        };

        // await sleep(1000);
        // console.log("body", body);
        const { data } = await gosafeApi.post("strat", body);

        dispatch(onLoadRisk(data));
        dispatch(setQuizAnswered(data));
        return;
      } catch (error) {
        return dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch, id]
  );

  const startSendQuizSatisfaction = async (anwers) => {
    try {
      dispatch(onChecking());
      const body = {
        patientSurgeryId: id,
        responses: anwers,
      };
      const { data } = await gosafeApi.post("evaluation/satisfaction", body);
      return dispatch(onLoadRisk(data));
    } catch (error) {
      return dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  };

  const startGetSurgeryRoomsByHeadquarterId = async (headquarterId) => {
    try {
      dispatch(onChecking());
      dispatch(onCleanSchedule());
      const { data } = await gosafeApi.get(
        `surgeryRoom/byHeadquarterId/${headquarterId}`
      );
      dispatch(onNotChecking());
      return data;
    } catch (error) {
      dispatch(onCleanSchedule());
      return dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  };

  const starGetDoctorsByHeadquarterId = async (headquarterId) => {
    try {
      dispatch(onChecking());
      dispatch(onCleanSchedule());

      const { data } = await gosafeApi.get(
        `doctor/byHeadquarterId/${headquarterId}`
      );

      dispatch(onNotChecking());
      return data;
    } catch (error) {
      dispatch(onNotChecking());
      return (
        translations[error.response.data.message] || error.response.data.message
      );
    }
  };

  const startGetScheduleBySurgeryRoomId = async ({ surgeryRoomId }) => {
    try {
      dispatch(onChecking());
      const { data } = await gosafeApi.get(
        `calendarEvents/bySurgeryRoomId/${surgeryRoomId}`
      );
      return dispatch(onLoadSchedule(data));
    } catch (error) {
      console.log(error);
      // dispatch(onErrorMessage(error.response.data.message));
    }
  };

  const startGetScheduleBySurgeryRoomIdPatientSurgeryId = useCallback(
    async ({ surgeryRoomId }) => {
      try {
        dispatch(onChecking());

        const { data } = await gosafeApi.get(
          `scheduleEvent/bySurgeryRoomIdPatientSurgeryId/${surgeryRoomId}/${id}`
        );

        dispatch(onLoadSchedule(data));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch, id]
  );

  const startCleanSchedule = () => {
    return dispatch(onCleanSchedule());
  };

  const startGetHeadquartersByName = async () => {
    try {
      dispatch(onChecking());
      dispatch(onCleanSchedule());
      const { data } = await gosafeApi.get(`headquarter`);
      dispatch(onNotChecking());
      return data;
    } catch (error) {
      dispatch(onNotChecking());
      return (
        translations[error.response.data.message] || error.response.data.message
      );
    }
  };

  const startUpdateScheduleEventNox = async (scheduleEventId, { file }) => {
    try {
      dispatch(onChecking());
      const body = {
        scheduleEventId,
      };

      const formData = new FormData();
      formData.append("file", file[0]);
      formData.append("data", JSON.stringify(body));

      const { data } = await gosafeApi.put(
        `patientSurgery/byScheduledAssessment/${id}`,
        formData
      );
      dispatch(onUpdateState(data.patientSurgery));
      return dispatch(
        onSuccesMessage(translations[data.message] || data.message)
      );
    } catch (error) {
      dispatch(onErrorMessage(error.response.data.message));
    }
  };

  const startScheduleAssessment = useCallback(
    async (scheduleEventId, { files }) => {
      try {
        dispatch(onChecking());

        const body = {
          scheduleEventId,
        };

        //console.log("files", files);
        // console.log(JSON.stringify(body));

        const validFiles = Array.from(files).filter(
          (file) =>
            file.type === "application/pdf" || file.type.startsWith("image/")
        );

        if (validFiles.length !== files.length) {
          dispatch(onErrorMessage("Solo se permiten imágenes o PDFs"));
          return;
        }

        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append("files", file);
        });
        formData.append("body", JSON.stringify(body));

        const { data } = await gosafeApi.put(
          `patientSurgery/byScheduledAssessment/${id}`,
          formData
        );

        dispatch(onUpdateState(data.patientSurgery));
        dispatch(addValorationEvent(data.scheduleEvent));
        dispatch(onSuccesMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch, id]
  );

  const startScheduleSessionFinal = async (scheduleEventId) => {
    try {
      dispatch(onChecking());

      const body = {
        scheduleEventId,
      };

      // console.log("body", body);
      // console.log("patientSurgeryId", id);
      // await sleep(2000);

      const { data } = await gosafeApi.put(
        `patientSurgery/byScheduledAssessmentFinal/${id}`,
        body
      );

      dispatch(onUpdateState(data.patientSurgery));
      return dispatch(
        onSuccesMessage(translations[data.message] || data.message)
      );
    } catch (error) {
      dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  };

  const startScheduledProcedure = useCallback(
    async (scheduleEventId) => {
      try {
        dispatch(onChecking());
        const body = {
          scheduleEventId,
          type: "PROCEDURE",
        };

        // console.log("body", body);
        // console.log("patientSurgeryId", id);
        // await sleep(2000);

        const { data } = await gosafeApi.put(
          `patientSurgery/byScheduledProcedure/${id}`,
          body
        );

        // console.log("data", data);

        // const data = {
        //   message: "scheduled event successfully",
        //   scheduleEvent: {
        //     id: body.scheduleEventId,
        //     title: "jejej",
        //     notes: "jejej",
        //     start: "jejej",
        //     end: "jejej",
        //     urlPdf: "jejej",
        //     state: "jejej",
        //     patientSurgeryId: "jejej",
        //     attended: "jejej",
        //   },
        // };

        if (
          procedure.procedureParentId === "d110d833-b4d2-11ef-a5cd-066d180a3717"
        ) {
          dispatch(onAddScheduledEventCount(1));
          dispatch(removeScheduledEvent(data));
          // dispatch(onSuccesMessage(translations[data.message] || data.message));
          // return;
        }

        dispatch(addProcedureEvent(data.scheduleEvent));
        dispatch(onSuccesMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch, id, procedure?.procedureParentName]
  );

  const startCancelProcedure = useCallback(
    async (scheduleEventId, eventsToRemove = 1) => {
      try {
        dispatch(onChecking());

        const body = {
          scheduleEventId,
          type: null,
          patientSurgeryId: null,
        };

        // console.log("body", body);
        // console.log("patientSurgeryId", id);
        // await sleep(2000);

        const { data } = await gosafeApi.put(
          `patientSurgery/byCancelProcedure/${id}`,
          body
        );

        // if (procedure?.procedureParentName === "Cámara hiperbarica") {
        //   dispatch(onRemoveScheduledEventCount(eventsToRemove));
        //   dispatch(onUpdateScheduleEvent(data));
        //   dispatch(onSuccesMessage(translations[data.message] || data.message));
        //   return;
        // }

        dispatch(removeProcedureEvent(scheduleEventId));
        dispatch(onRemoveScheduledEventCount(eventsToRemove));
        dispatch(onSuccesMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch, id]
  );

  const startViewVideoProcedure = () => {
    dispatch(onViewVideoProcedure());
  };

  const startViewPdfProcedure = async () => {
    await sleep(5000);
    dispatch(onViewPdfProcedure());
  };

  const startCancelScheduledAssessment = useCallback(
    async (scheduleEventId) => {
      try {
        dispatch(onChecking());

        const body = {
          scheduleEventId,
          type: null,
          patientSurgeryId: null,
        };

        // console.log("patientSurgeryId", id);
        // console.log("body", body);

        const { data } = await gosafeApi.put(
          `patientSurgery/byCancelScheduledAssessment/${id}`,
          body
        );

        dispatch(onUpdateState(data.patientSurgery));
        dispatch(removeValorationEvent(scheduleEventId));
        dispatch(onSuccesMessage(translations[data.message] || data.message));
        // dispatch(onErrorMessage("Hola"));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
      }
    },
    [dispatch, id]
  );

  const startCancelScheduledAssessmentFinal = async () => {
    try {
      dispatch(onChecking());
      // console.log("patientSurgeryId", id);

      const { data } = await gosafeApi.put(
        `patientSurgery/byCancelScheduledAssessmentFinal/${id}`
      );
      dispatch(onUpdateState(data.patientSurgery));
      return dispatch(
        onSuccesMessage(translations[data.message] || data.message)
      );
    } catch (error) {
      dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  };

  const startTour = useCallback(
    (steps) => {
      try {
        setCurrentStep(0);
        setSteps(steps);
        if (isTutorialOpen) {
          setIsOpen(true);
          return;
        }
        setIsOpen(false);
      } catch (error) {
        console.log("error", error);
        setIsOpen(false);
      }
    },
    [isTutorialOpen, setCurrentStep, setSteps, setIsOpen]
  );

  const startInvertTutorial = useCallback(() => {
    if (isTutorialOpen) {
      dispatch(closeTutorial());
      setIsOpen(false);
      return;
    }
    dispatch(openTutorial());
    setIsOpen(true);
  }, [dispatch, isTutorialOpen, setIsOpen]);

  const startCancelScheduleProcedurePainBomb = useCallback(async () => {
    try {
      dispatch(onChecking());

      // console.log("patientSurgeryId", id);
      // await sleep(2000);

      const { data } = await gosafeApi.put(
        `patientSurgery/byCancelProcedurePainBomb/${id}`
      );

      dispatch(onRemoveScheduledEventCount(1));
      dispatch(onSuccesMessage(translations[data.message] || data.message));
    } catch (error) {
      dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  }, [dispatch, id]);

  const startCancelLastScheduleProcedure = useCallback(async () => {
    try {
      dispatch(onChecking());

      const { data } = await gosafeApi.put(
        `patientSurgery/byCancelLastProcedure/${id}`
      );

      dispatch(onRemoveScheduledEventCount(1));
      dispatch(onSuccesMessage(translations[data.message] || data.message));
    } catch (error) {
      dispatch(
        onErrorMessage(
          translations[error.response.data.message] ||
            error.response.data.message
        )
      );
    }
  }, [dispatch, id]);

  const addScheduledEventCount = useCallback(
    (eventsToAdd) => {
      dispatch(onAddScheduledEventCount(eventsToAdd));
    },
    [dispatch]
  );

  const startCancelViewMedia = useCallback(() => {
    dispatch(onNotViewMedia());
  }, [dispatch]);

  const getDoctorsListByIpsId = useCallback(
    async ({ ipsId }) => {
      try {
        dispatch(loadingDoctorsSelect());

        const { data } = await gosafeApi.get(`doctor/listByIpsId/${ipsId}`);

        dispatch(loadedDoctorsSelect(data));
        return;
      } catch (error) {
        dispatch(
          failedDoctorsSelect(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch]
  );

  const getScheduleByDoctorHeadquarterScheduleId = useCallback(
    async ({ doctorHeadquarterScheduleId }) => {
      try {
        dispatch(onChecking());
        dispatch(onCleanSchedule());

        const { data } = await gosafeApi.get(
          `patientSurgery/schedule/byDoctorHeadquarterScheduleIdPatientSurgeryId/${doctorHeadquarterScheduleId}/${id}`
        );
        dispatch(onLoadSchedule(data));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error?.response?.data?.message] ||
              error?.response?.data?.message ||
              error?.message
          )
        );
        return;
      }
    },
    [dispatch, id]
  );

  const startResetProcedureScheduled = useCallback(async () => {
    await sleep(3000);
    dispatch(resetProcedureScheduled());
  }, [dispatch]);

  return {
    //Props
    isLoadingQuiz,
    isQuiz,
    message,
    patient,
    procedure,
    state,
    track,
    dasiScore,
    mets,
    cRisk,
    asa,
    summary,
    checking,
    errorMessage,
    surgeryRooms,
    schedule,
    description,
    numberEvents,
    scheduledEventCount,
    scheduledEventFinishedCount,
    viewVideoProcedure,
    viewPdfProcedure,
    isTutorialOpen,
    finishedProcedureEventsCount,
    assessmentEvents,
    procedureEvents,
    finishedProcedure,
    doctorsSelect,
    isProcedureScheduled,
    patientSurgery,

    //Methods
    startDecryptKey,
    startSendQuiz,
    startSendQuizSatisfaction,
    startGetSurgeryRoomsByHeadquarterId,
    starGetDoctorsByHeadquarterId,
    startGetScheduleBySurgeryRoomId,
    startCleanSchedule,
    startGetHeadquartersByName,
    startUpdateScheduleEventNox,
    startScheduleSessionFinal,
    startGetScheduleBySurgeryRoomIdPatientSurgeryId,
    startScheduledProcedure,
    startViewVideoProcedure,
    startViewPdfProcedure,
    startCancelProcedure,
    startCancelScheduledAssessment,
    startCancelScheduledAssessmentFinal,
    startTour,
    startInvertTutorial,
    startCancelScheduleProcedurePainBomb,
    startCancelLastScheduleProcedure,
    addScheduledEventCount,
    startCancelViewMedia,
    startScheduleAssessment,
    getDoctorsListByIpsId,
    getScheduleByDoctorHeadquarterScheduleId,
    startResetProcedureScheduled,
  };
};
